import { MoneyValue } from './MoneyValue';
import { formatDateTime, formatDate } from '@/lib/format';
import { tenderLabel } from '@/lib/tenderLabel';
import { Separator } from '@/components/ui/separator';
import type { SaleDetail } from '@/types/sale';

type ReceiptItem = NonNullable<SaleDetail['items']>[number];

interface ReceiptPreviewProps {
  detail: SaleDetail;
}

/**
 * Collapse identical line items into one row with a summed quantity — same
 * product name AND same unit price merge (e.g. 3× "Virtual Reality" at Rp 35.000
 * from per-unit add-on slots → one "×3" line). Items that carry their own
 * add-ons are left un-merged so each line keeps its own add-on mapping; a
 * different unit price (e.g. slot-based pricing) also stays a separate line.
 * Order is preserved.
 */
function mergeReceiptItems(items: ReceiptItem[]): ReceiptItem[] {
  const merged: ReceiptItem[] = [];
  const indexByKey = new Map<string, number>();
  for (const it of items) {
    if ((it.addOns?.length ?? 0) > 0) {
      merged.push({ ...it });
      continue;
    }
    const key = `${it.productName ?? ''}|${it.price ?? 0}`;
    const at = indexByKey.get(key);
    if (at == null) {
      indexByKey.set(key, merged.length);
      merged.push({ ...it });
    } else {
      const cur = merged[at]!;
      merged[at] = { ...cur, quantity: cur.quantity + it.quantity };
    }
  }
  return merged;
}

/**
 * One label/value line on the receipt.
 *
 * `justify-between` alone was not enough. A value long enough to wrap — a
 * tender named "Kartu Debit/Kredit (EDC Permata)", or simply a customer with a
 * long name — grew until it met the label, and with no gap between them the
 * two ran together as "PembayaranKartu Debit/Kredit (EDC". So the label never
 * shrinks, there is always a gap, and a wrapped value stays right-aligned
 * under itself instead of drifting back toward the label.
 */
function MetaRow({
  label,
  value,
  numeric = false,
}: {
  label: string;
  value: string;
  numeric?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-ink-400">{label}</dt>
      <dd className={`min-w-0 text-right${numeric ? ' tnum' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * The inclusive tax note — MoM 16/10, "untuk struk tiket tambahkan PBJT".
 *
 * NOT an addend. Park prices contain the tax, so this figure is already inside
 * TOTAL: `Subtotal + tax` deliberately does NOT equal TOTAL, which is why the
 * note sits BELOW the total and is worded "Termasuk …". (The invoice PDF
 * printed this row above "Subtotal" once and it read as something the total was
 * still missing.)
 *
 * The word is the server's — PPN for an older sale or a mixed cart with
 * add-ons, PBJT for an entry-tickets-only sale once the park's ticket tax is
 * on — so this surface cannot invent a label the documents disagree with. The
 * rate is the one THIS sale was sold under; `id-ID` writes a decimal with a
 * comma ("10,5%"), and an un-derivable rate prints the bare type.
 */
function taxNote(tax: NonNullable<SaleDetail['tax']>): string {
  const pct = typeof tax.percent === 'number' && tax.percent > 0 ? tax.percent : null;
  return pct == null
    ? `Termasuk ${tax.type}`
    : `Termasuk ${tax.type} ${String(pct).replace('.', ',')}%`;
}

function paymentLabel(detail: SaleDetail): string | null {
  // The resolved method name from the DB wins; the gateway map is the fallback
  // and is shared with the shift summary (`@/lib/tenderLabel`) so the two
  // surfaces cannot drift apart on what a tender is called.
  if (detail.paymentMethodName) return detail.paymentMethodName;
  return tenderLabel(detail.paymentGateway);
}

/** On-screen receipt mock (Poppins brand) mirroring the printed collateral. */
export function ReceiptPreview({ detail }: ReceiptPreviewProps) {
  const method = paymentLabel(detail);
  return (
    <div className="mx-auto w-full max-w-xs rounded-lg border border-dashed border-ink-400/40 bg-white p-5 font-brand text-ink-900 shadow-sm">
      <div className="text-center">
        <img
          src="/logo-autopia-red.png"
          alt="Autopia Museum & Parks"
          className="mx-auto h-9 w-auto object-contain"
        />
        <p className="mt-2 font-display text-sm font-bold text-ink-900">
          Autopia Museum &amp; Parks
        </p>
        <p className="mt-0.5 text-[10px] leading-snug text-ink-400">
          Jl. Raya Lembang No.272, Lembang,
          <br />
          Bandung Barat, Jawa Barat
          <br />
          Indonesia 40391
        </p>
        <p className="mt-2 text-[11px] uppercase tracking-widest text-ink-400">Bukti Pembayaran</p>
        <p className="tnum mt-1 font-display text-[11px] font-bold text-ink-900">{detail.invoiceId}</p>
      </div>
      <Separator className="my-3 border-dashed" />
      <dl className="space-y-1 text-xs">
        <MetaRow label="Tanggal" value={formatDateTime(detail.createdAt)} numeric />
        {detail.visitDate && (
          <MetaRow label="Kunjungan" value={formatDate(detail.visitDate)} numeric />
        )}
        <MetaRow
          label="Pelanggan"
          value={detail.userInfo?.name || detail.userInfo?.phone || '—'}
        />
        <MetaRow label="Kasir" value={detail.cashierName || '—'} />
        {method && <MetaRow label="Pembayaran" value={method} />}
      </dl>
      <Separator className="my-3 border-dashed" />
      <ul className="space-y-1 text-xs">
        {mergeReceiptItems(detail.items ?? []).map((it, i) => (
          <li key={i}>
            <div className="flex justify-between gap-2">
              <span className="truncate">
                {it.productName} <span className="tnum text-ink-400">×{it.quantity}</span>
              </span>
              <MoneyValue value={(it.price ?? 0) * it.quantity} className="text-xs" />
            </div>
            {(it.addOns ?? []).map((a, j) => (
              <div key={j} className="flex justify-between gap-2 pl-3 text-[11px] text-ink-400">
                <span className="truncate">+ {a.productName}</span>
                {a.included ? (
                  <span className="shrink-0 italic">Termasuk</span>
                ) : (
                  <MoneyValue value={a.price * a.quantity} className="text-[11px] text-ink-400" />
                )}
              </div>
            ))}
          </li>
        ))}
      </ul>
      <Separator className="my-3 border-dashed" />
      {(detail.discountAmount ?? 0) > 0 && (
        <dl className="mb-1 space-y-0.5 text-xs">
          <div className="flex justify-between">
            <dt className="text-ink-400">Subtotal</dt>
            <dd className="tnum">
              <MoneyValue value={detail.originalAmount ?? detail.totalAmount} className="text-xs" />
            </dd>
          </div>
          {(detail.discounts?.length ?? 0) > 0 ? (
            detail.discounts!.map((d, i) => (
              <div key={i} className="flex justify-between gap-2 text-success">
                <dt className="min-w-0 truncate">
                  {d.name}
                  {d.couponCode && (
                    <span className="text-ink-400"> ({d.couponCode})</span>
                  )}
                </dt>
                <dd className="tnum shrink-0">
                  − <MoneyValue value={d.amount} className="text-xs text-success" />
                </dd>
              </div>
            ))
          ) : (
            <div className="flex justify-between text-success">
              <dt>Diskon</dt>
              <dd className="tnum">
                − <MoneyValue value={detail.discountAmount} className="text-xs text-success" />
              </dd>
            </div>
          )}
        </dl>
      )}
      <div className="flex items-center justify-between">
        <span className="font-display text-sm font-bold uppercase">Total</span>
        <MoneyValue value={detail.totalAmount} emphasis className="text-lg" />
      </div>
      {detail.tax && detail.tax.amount > 0 && (
        <div className="mt-1 flex justify-between gap-2 text-[11px] text-ink-400">
          <span className="min-w-0 truncate">{taxNote(detail.tax)}</span>
          <MoneyValue value={detail.tax.amount} className="text-[11px] text-ink-400" />
        </div>
      )}
      {detail.cash && (
        <dl className="mt-1 space-y-0.5 text-xs">
          <div className="flex justify-between">
            <dt className="text-ink-400">Tunai</dt>
            <dd className="tnum"><MoneyValue value={detail.cash.amountTendered} className="text-xs" /></dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-400">Kembali</dt>
            <dd className="tnum"><MoneyValue value={detail.cash.changeDue} className="text-xs" /></dd>
          </div>
        </dl>
      )}
      <p className="mt-4 text-center text-[10px] text-ink-400">Terima kasih atas kunjungan Anda.</p>
    </div>
  );
}
